import { CardActions, CircularProgress, createStyles, makeStyles, Theme } from '@material-ui/core';
import CommentIconMUI from '@material-ui/icons/Comment';
import MuiCreateIcon from '@material-ui/icons/Create';
import MuiDeleteIcon from '@material-ui/icons/Delete';
import MuiGroupIcon from '@material-ui/icons/Group';
import MoreVertIconMUI from '@material-ui/icons/MoreVert';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbUp';
import VisibilityIconMUI from '@material-ui/icons/Visibility';
import classNames from 'classnames';
import React from 'react';

import { ReactComponent as AddAnswerSvg } from '../../../../assets/add_answer.svg';
import { ReactComponent as PublishSvg } from '../../../../assets/publish.svg';
import { ReactComponent as ShareSvg } from '../../../../assets/share.svg';
import { ReactComponent as UnpublishSvg } from '../../../../assets/unpublish.svg';
import {
  FeedAnswerFragment,
  FeedQuestionDetailsFragment,
  PlexOverviewFragment,
  QuestionStatus,
  ReactionType,
  useDeleteQuestionMutation,
  usePublishFactMutation,
  useReactToQuestionMutation,
  useWatchQuestionMutation,
} from '../../../../graphql/gen/types';
import { usePublishStatusFilter } from '../../../../models/filters/usePublishStatusFilter';
import { useSelectedLabelIds } from '../../../../models/filters/useSelectedLabelIds';
import { useConfirmAction } from '../../../../providers/plex_provider';
import { colorPalette, spacings } from '../../../../theme';
import { ButtonColor, PlexButton } from '../../../shared/primitives/PlexButton';
import { IconButtonSize, PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { useSelectedPlexQuestion } from '../../details/question/useSelectedQuestion';
import { useDialogState } from '../../dialog/useDialogState';
import { useSharePopover } from '../../feed/useSharePopover';
import { canDeleteQuestion, canEditAnswer, canPublishFact, canUnpublishFact } from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';
import { MoreOptionsMenu } from '../../shared/MoreOptionsMenu';
import { SharePopover } from '../SharePopover';

type Props = {
  question: FeedQuestionDetailsFragment;
  plexData: PlexOverviewFragment;
};

export function QuestionViewFooter(props: Props): React.ReactElement {
  const { question, plexData } = props;
  const plexRelation = plexData.currentUserRelation.state;

  const { id, status } = question;
  const { id: plexId } = plexData;
  const [shareAnchorEl, handleClickShare, handleCloseShare] = useSharePopover();
  const classes = useStyles();

  const filterPublishStatus = usePublishStatusFilter();
  const labelIds = useSelectedLabelIds();
  const [, setSelectedPlexQuestion] = useSelectedPlexQuestion();
  const [, setDialogState] = useDialogState();
  const [publishFact, { loading: publishFactLoading }] = usePublishFactMutation();
  const [watchQuestion, { loading: watchLoading }] = useWatchQuestionMutation();
  const [reactToQuestionMutation] = useReactToQuestionMutation();
  const [deleteQuestion] = useDeleteQuestionMutation();
  const confirmAction = useConfirmAction();

  const handleOpenComments = (): void => {
    setSelectedPlexQuestion({ permalink: question.permalink, commentsVisible: true });
  };

  const handleAddAnswer = (): void => {
    handleOpenComments();
    setDialogState({ type: 'new_fact_answer', factId: id });
  };

  const handlePublish = (): void => {
    publishFact({
      variables: {
        publishQuestionInput: {
          questionId: question.id,
          checked: true,
        },
      },
    }).then(() => logAnalytics({ name: 'faq published', details: { plexId, itemId: id } }));
  };

  const handlePostInternally = (): void => {
    publishFact({
      variables: {
        publishQuestionInput: {
          questionId: question.id,
          checked: false,
        },
      },
    }).then(() => logAnalytics({ name: 'faq unpublished', details: { plexId, itemId: id } }));
  };

  const currentUserLiked = question.currentUserReactions.includes(ReactionType.Like);
  const handleLike = (): void => {
    reactToQuestionMutation({
      variables: { reaction: { type: ReactionType.Like, questionId: question.id, checked: !currentUserLiked } },
    }).then(() => logAnalytics({ name: 'faq liked', details: { plexId, itemId: id } }));
  };

  const acceptedAnswer: FeedAnswerFragment | null =
    question.acceptedAnswer != null && !question.acceptedAnswer.deleted ? question.acceptedAnswer : null;

  const handleEditAnswer = (): void => {
    if (!!acceptedAnswer) {
      setDialogState({ type: 'edit_fact_answer', factAnswerId: acceptedAnswer.id });
    }
  };

  const handleDelete = (): void => {
    confirmAction({
      title: 'Confirmation',
      message: 'Are you sure you want to permanently delete this question?',
      confirmButton: {
        onClick: (): void => {
          deleteQuestion({
            variables: { questionId: id, filterPublishStatus, labelIds },
          }).then(() => logAnalytics({ name: 'faq deleted', details: { plexId, itemId: id } }));
        },
        label: 'Delete',
        color: ButtonColor.RedBackground,
      },
    });
  };

  const handleWatch = (): void => {
    watchQuestion({
      variables: {
        payload: {
          questionId: question.id,
          watching: !question.watching,
        },
      },
    });
    logAnalytics({
      name: question.watching ? 'faq watching removed' : 'faq watching added',
      details: { plexId, itemId: question.id },
    });
  };

  const commentsCount = question.commentsCount || 0;

  return (
    <CardActions className={classes.actionsContainer}>
      <div className={classes.actionsLeftContainer}>
        {!!acceptedAnswer ? (
          <PlexButton
            onClick={handleOpenComments}
            label={`Comment (${commentsCount})`}
            MuiIconLeft={CommentIconMUI}
            color={ButtonColor.TransparentGray}
            tooltip={'Add a comment to this question'}
          />
        ) : (
          <PlexButton
            onClick={handleAddAnswer}
            label={'Answer'}
            SvgIconLeft={AddAnswerSvg}
            color={ButtonColor.TransparentGray}
            tooltip={'Add an answer to this question'}
          />
        )}
      </div>
      <div className={classes.actionsRightContainer}>
        <PlexIconButton
          onClick={handleWatch}
          MuiIcon={VisibilityIconMUI}
          disabled={watchLoading}
          color={question.watching ? ButtonColor.TransparentGreen : ButtonColor.TransparentGray}
          tooltip={question.watching ? 'Stop watching' : 'Watch'}
          size={IconButtonSize.Normal}
        />
        <PlexIconButton
          onClick={handleClickShare}
          SvgIcon={ShareSvg}
          color={ButtonColor.TransparentGray}
          tooltip={'Share this question'}
          size={IconButtonSize.Normal}
        />
        <SharePopover itemId={id} itemType="question" anchorEl={shareAnchorEl} handleClose={handleCloseShare} />

        <MoreOptionsMenu
          id={'options_faq'}
          Button={PlexIconButton}
          size={IconButtonSize.Normal}
          MuiIcon={MoreVertIconMUI}
          tooltip={'Open more options for this FAQ'}
          color={ButtonColor.TransparentGray}
          options={[
            status === QuestionStatus.Draft
              ? {
                  label: (
                    <>
                      <MuiGroupIcon className={classes.moreOptionIcon} />
                      <span>Post Internally</span>
                    </>
                  ),
                  onClick: handlePostInternally,
                }
              : null,
            !!acceptedAnswer && canPublishFact({ plexRelation, status })
              ? {
                  label: (
                    <>
                      {publishFactLoading && <CircularProgress size={24} className={classes.publishLoading} />}
                      <PublishSvg className={classes.moreOptionIcon} />
                      <span>{publishFactLoading ? 'Publishing...' : 'Publish'}</span>
                    </>
                  ),
                  onClick: handlePublish,
                }
              : null,
            canUnpublishFact({ plexRelation, status })
              ? {
                  label: (
                    <>
                      <UnpublishSvg className={classes.moreOptionIcon} />
                      <span>Unpublish</span>
                    </>
                  ),
                  onClick: handlePostInternally,
                }
              : null,
            !!acceptedAnswer
              ? {
                  label: (
                    <>
                      <AddAnswerSvg className={classes.moreOptionIcon} />
                      <span>Add Answer</span>
                    </>
                  ),
                  onClick: handleAddAnswer,
                }
              : {
                  label: (
                    <>
                      <CommentIconMUI className={classes.moreOptionIcon} />
                      <span>{`Comment (${commentsCount})`}</span>
                    </>
                  ),
                  onClick: handleOpenComments,
                },
            !!acceptedAnswer && canEditAnswer({ plexRelation, answerRelation: acceptedAnswer.currentUserRelation })
              ? {
                  label: (
                    <>
                      <MuiCreateIcon className={classes.moreOptionIcon} />
                      <span>Edit Answer</span>
                    </>
                  ),
                  onClick: handleEditAnswer,
                }
              : null,
            {
              label: currentUserLiked ? (
                <>
                  <ThumbDownIcon className={classes.moreOptionIcon} />
                  <span>Unlike</span>
                </>
              ) : (
                <>
                  <ThumbUpIcon className={classes.moreOptionIcon} />
                  <span>Like</span>
                </>
              ),
              onClick: handleLike,
            },
            canDeleteQuestion({ plexRelation, questionRelation: question.currentUserRelation })
              ? {
                  label: (
                    <>
                      <MuiDeleteIcon className={classNames(classes.moreOptionIcon, classes.deleteButton)} />
                      <span className={classes.deleteButton}>Delete</span>
                    </>
                  ),
                  onClick: handleDelete,
                }
              : null,
          ]}
          menuProps={{
            marginThreshold: 0,
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
            transformOrigin: { horizontal: 'right', vertical: 'bottom' },
          }}
        />
      </div>
    </CardActions>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionsContainer: {
      justifyContent: 'space-between',
      padding: 0,
      borderWidth: 0,
      boxShadow: 'none',
      marginLeft: spacings.small,
      marginRight: spacings.small,
      marginBottom: spacings.small,
    },
    actionsLeftContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
    actionsRightContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
    publishLoading: {
      marginRight: spacings.normal,
    },
    moreOptionIcon: {
      fill: `${colorPalette.purpleGray._500} !important`,
      marginRight: spacings.small,
      width: IconButtonSize.Small,
      height: IconButtonSize.Small,
    },
    deleteButton: {
      fill: `${colorPalette.red._400} !important`,
      color: colorPalette.red._400,
    },
  })
);
